import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, GripVertical, HelpCircle } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableFAQ = ({ faq, onEdit, onDelete, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: faq.faq_id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : 0, opacity: isDragging ? 0.8 : 1 };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className={`border-cfc-border ${isDragging ? "shadow-lg ring-2 ring-cfc-accent" : ""}`} data-testid={`faq-admin-${faq.faq_id}`}>
        <CardContent className="p-4 flex items-start gap-3">
          <button className="cursor-grab active:cursor-grabbing text-cfc-text-muted hover:text-cfc-primary mt-1 touch-none" {...attributes} {...listeners}>
            <GripVertical size={16} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`font-medium text-sm ${faq.is_active ? "text-cfc-primary" : "text-cfc-text-muted line-through"}`}>
                {faq.question}
              </span>
              {faq.question_es && <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">ES</span>}
            </div>
            <p className="text-xs text-cfc-text-muted line-clamp-2">{faq.answer}</p>
          </div>
          <Switch checked={faq.is_active} onCheckedChange={() => onToggle(faq)} />
          <Button variant="ghost" size="sm" onClick={() => onEdit(faq)}><Edit size={14} /></Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(faq.faq_id)} className="text-cfc-destructive"><Trash2 size={14} /></Button>
        </CardContent>
      </Card>
    </div>
  );
};

const FAQManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ question: "", answer: "", question_es: "", answer_es: "", is_active: true, order: 0 });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchFaqs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/faq`, { withCredentials: true });
      setFaqs(res.data);
    } catch (e) {
      console.error("Failed to fetch FAQs:", e);
    }
  }, []);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  const openDialog = (faq = null) => {
    if (faq) {
      setEditing(faq);
      setFormData({ question: faq.question, answer: faq.answer, question_es: faq.question_es || "", answer_es: faq.answer_es || "", is_active: faq.is_active, order: faq.order || 0 });
    } else {
      setEditing(null);
      setFormData({ question: "", answer: "", question_es: "", answer_es: "", is_active: true, order: faqs.length });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) { toast.error("Question and answer are required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/faq/${editing.faq_id}`, formData, { withCredentials: true });
        toast.success("FAQ updated");
      } else {
        await axios.post(`${API}/faq`, formData, { withCredentials: true });
        toast.success("FAQ created");
      }
      setIsDialogOpen(false);
      fetchFaqs();
    } catch (e) { toast.error("Failed to save FAQ"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await axios.delete(`${API}/faq/${id}`, { withCredentials: true });
      toast.success("FAQ deleted");
      fetchFaqs();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleToggle = async (faq) => {
    try {
      await axios.put(`${API}/faq/${faq.faq_id}`, { is_active: !faq.is_active }, { withCredentials: true });
      fetchFaqs();
    } catch (e) { toast.error("Failed to update"); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const sorted = [...faqs].sort((a, b) => a.order - b.order);
    const oldIdx = sorted.findIndex((f) => f.faq_id === active.id);
    const newIdx = sorted.findIndex((f) => f.faq_id === over.id);
    const reordered = arrayMove(sorted, oldIdx, newIdx);
    setFaqs(reordered.map((f, i) => ({ ...f, order: i })));
    // Persist order
    for (let i = 0; i < reordered.length; i++) {
      await axios.put(`${API}/faq/${reordered[i].faq_id}`, { order: i }, { withCredentials: true }).catch(() => {});
    }
  };

  const sortedFaqs = useMemo(() => faqs.slice().sort((a, b) => a.order - b.order), [faqs]);

  return (
    <div data-testid="faq-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">FAQ Manager</h1>
          <p className="text-cfc-text-muted text-sm mt-1">Manage frequently asked questions (English + Spanish)</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-cfc-accent hover:bg-cfc-accent-hover text-cfc-primary" data-testid="add-faq-btn">
          <Plus size={18} className="mr-2" /> Add FAQ
        </Button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortedFaqs.map((f) => f.faq_id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {sortedFaqs.map((faq) => (
              <SortableFAQ key={faq.faq_id} faq={faq} onEdit={openDialog} onDelete={handleDelete} onToggle={handleToggle} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {faqs.length === 0 && (
        <div className="text-center py-12 text-cfc-text-muted">
          <HelpCircle size={32} className="mx-auto mb-3 opacity-50" />
          <p>No FAQ items yet.</p>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="faq-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">{editing ? "Edit FAQ" : "New FAQ"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="border-b pb-3">
              <Label className="text-xs uppercase tracking-wider text-cfc-text-muted mb-2 block">English</Label>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="faq-q">Question</Label>
                  <Input id="faq-q" value={formData.question} onChange={(e) => setFormData({ ...formData, question: e.target.value })} placeholder="What ages do you accept?" data-testid="faq-question-input" />
                </div>
                <div>
                  <Label htmlFor="faq-a">Answer</Label>
                  <Textarea id="faq-a" value={formData.answer} onChange={(e) => setFormData({ ...formData, answer: e.target.value })} placeholder="We have teams for..." rows={3} data-testid="faq-answer-input" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-blue-600 mb-2 block">Spanish (Espanol)</Label>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="faq-q-es">Pregunta</Label>
                  <Input id="faq-q-es" value={formData.question_es} onChange={(e) => setFormData({ ...formData, question_es: e.target.value })} placeholder="Spanish translation..." data-testid="faq-question-es-input" />
                </div>
                <div>
                  <Label htmlFor="faq-a-es">Respuesta</Label>
                  <Textarea id="faq-a-es" value={formData.answer_es} onChange={(e) => setFormData({ ...formData, answer_es: e.target.value })} placeholder="Spanish translation..." rows={3} data-testid="faq-answer-es-input" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={handleSubmit} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="save-faq-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FAQManager;
